var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { scopedWarningMessage } from '@philips-internal/oswald-ui-utils';
import { AwesomeEvent, getEventList } from './component/awesome-event.js';
import { configureEventListener } from './component/event/event-listener.js';
import { MountEventListener } from './component/types/mountable.js';
import { AuthenticationService } from './services/authentication-service/authentication-service.js';
import { SessionStorageService } from './services/session-storage-service/session-storage-service.js';
const CHILDREN_APIS = Symbol('children-apis');
const EVENT_DELEGATORS = Symbol('event-delegators');
const SSOT = Symbol('single-source-of-truth');
const LIFECYCLE_EVENTS = ['onMount', 'onUnmount'];
function componentAPIError(message) {
    return new Error(`[Component API]: ${message}`);
}
function configureAPI(configuration) {
    const { eventDelegator, getChildrenAPIs, instance, ssot } = configuration;
    if (instance[SSOT] || instance[EVENT_DELEGATORS] || instance[CHILDREN_APIS]) {
        throw componentAPIError('Component is already configured.');
    }
    instance[SSOT] = ssot;
    instance[EVENT_DELEGATORS] = eventDelegator;
    instance[CHILDREN_APIS] = getChildrenAPIs;
    const eventList = getEventList(instance).concat(LIFECYCLE_EVENTS);
    for (const type of eventList) {
        // @ts-ignore: Ignoring because the `type` variable can be any of our available events.
        const eventListener = instance[type];
        configureEventListener(eventListener, { api: instance, eventDelegator, type });
    }
}
class ComponentAPI {
    constructor() {
        this.onMount = new MountEventListener();
        this.onUnmount = new MountEventListener();
    }
    getAlias() {
        // @ts-ignore: the ts-ignore is needed to not cause an error in single-source-of-truth apply method.
        return this.state.alias;
    }
    setState(data) {
        this[SSOT].applyAndNotify(data);
    }
    get state() {
        return this[SSOT].state;
    }
    get refs() {
        return this[SSOT].refs;
    }
    get childrenAPIs() {
        return this[CHILDREN_APIS]();
    }
    setClassName(className) {
        // @ts-ignore: Need to think how to give this method only to components that have a className, without duplicating code.
        this.setState({ className });
    }
    getClassName() {
        // @ts-ignore: Need to think how to give this method only to components that have a className, without duplicating code.
        return this.state.className;
    }
    persistState(ignoreAuthentication = false) {
        if (!AuthenticationService.isAuthenticated() && !ignoreAuthentication) {
            scopedWarningMessage('component-api', 'Trying to persist the component state while not authenticated.', `If this is the correct behavior, please use the 'ignoreAuthentication' flag.`);
            return;
        }
        const state = { ...this[SSOT].props, ...this[SSOT].volatileState };
        SessionStorageService.setItem(this[SSOT].hashCode, state);
    }
    restoreState(clear = false) {
        const state = SessionStorageService.getItem(this[SSOT].hashCode);
        if (state) {
            this.setState(state);
            clear && this.clearPersistedState();
            return true;
        }
        return false;
    }
    clearPersistedState() {
        SessionStorageService.removeItem(this[SSOT].hashCode);
    }
    discard() {
        // ??? ¯\_(ツ)_/¯
    }
    setFormatterByProperty(property, name, options) {
        // @ts-ignore: Need to think how to give this method only to components that have a formatter, without duplicating code.
        const { formatters = {} } = this.state;
        // @ts-ignore: Need to think how to give this method only to components that have a formatter, without duplicating code.
        const formatter = {
            name,
            options,
        };
        // @ts-ignore: Need to think how to give this method only to components that have a formatter, without duplicating code.
        this.setState({ formatters: { ...formatters, [property]: formatter } });
    }
    getFormatterByProperty(property) {
        // @ts-ignore: Need to think how to give this method only to components that have a formatter, without duplicating code.
        const { formatters = {} } = this.state;
        // @ts-ignore: Need to think how to give this method only to components that have a formatter, without duplicating code.
        return formatters[property];
    }
    getFormatters() {
        // @ts-ignore: Need to think how to give this method only to components that have a formatter, without duplicating code.
        return this.state.formatters;
    }
    removeFormatterByProperty(property) {
        // @ts-ignore: Need to think how to give this method only to components that have a formatter, without duplicating code.
        const { formatters = {} } = this.state;
        // @ts-ignore: Need to think how to give this method only to components that have a formatter, without duplicating code.
        const newFormatters = { ...formatters };
        delete newFormatters[property];
        // @ts-ignore: Need to think how to give this method only to components that have a formatter, without duplicating code.
        this.setState({ formatters: newFormatters });
    }
}
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], ComponentAPI.prototype, "onMount", void 0);
__decorate([
    AwesomeEvent,
    __metadata("design:type", Object)
], ComponentAPI.prototype, "onUnmount", void 0);
export { ComponentAPI, configureAPI, };
export default ComponentAPI;
//# sourceMappingURL=component-api.js.map